import { useState, useEffect } from 'react'
import { HorseImageService } from '../services/horseImageService'
import i18n from '../i18n'

interface UseHorseImageReturn {
  imageUrl: string | null
  loading: boolean
  error: string | null
  retry: () => void
}

/**
 * Hook para gerenciar o carregamento de imagens de cavalos
 */
export const useHorseImage = (
  tenantId: number | null, 
  horseId: number | null,
  autoLoad: boolean = true
): UseHorseImageReturn => {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const preloadImage = (url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        console.log('✅ Imagem pré-carregada com sucesso')
        resolve()
      }
      img.onerror = () => {
        console.error('❌ Erro no pré-carregamento da imagem')
        reject(new Error('Erro ao pré-carregar imagem'))
      }
      img.src = url
    })
  }

  const loadImage = async () => {
    if (!tenantId || !horseId) {
      setImageUrl(null)
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const image = await HorseImageService.fetchHorseImage(tenantId, horseId)
      
      if (image) {
        // Pré-carregar a imagem antes de definir no estado
        await preloadImage(image)
        setImageUrl(image)
        setError(null)
      } else {
        setImageUrl(null)
        setError(i18n.t('horse.imageNotFound'))
      }
    } catch (err) {
      console.error('❌ Erro no hook useHorseImage:', err)
      setImageUrl(null)
      setError(i18n.t('horse.imageLoadError'))
    } finally {
      setLoading(false)
    }
  }

  const retry = () => {
    if (tenantId && horseId) {
      // Remove do cache para forçar nova requisição
      HorseImageService.removeCacheEntry(tenantId, horseId)
      loadImage()
    }
  }

  useEffect(() => {
    if (autoLoad) {
      loadImage()
    }
  }, [tenantId, horseId, autoLoad])

  return { imageUrl, loading, error, retry }
}